import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, Suspense } from "react";
import { Menu, X, ChevronDown, Accessibility, MessageCircle } from "lucide-react";
import { BrandSearch, BrandAccount, BrandHeart, BrandDelivery } from "./BrandIcons";
import { motion, AnimatePresence } from "framer-motion";
import { CartDrawer } from "./CartDrawer";
import { MegaMenu, visibleMegaMenu } from "./MegaMenu";
import { SearchOverlay } from "./SearchOverlay";
import { lazyRetry } from "@/lib/lazyRetry";
import { Button } from "./ui/button";
import { useWishlistStore } from "@/stores/wishlistStore";
import { useFloatingPanels } from "@/stores/floatingPanelsStore";
import { isEmptyCategoryHref, useCategoryStockVersion, withoutEmptyCategories } from "@/lib/categoryStock";
import { getSupabase } from "@/integrations/supabase/lazy";
import { FREE_SHIPPING_COPY } from "@/lib/shipping";

interface MobileNavSection {
  heading?: string;
  items: { label: string; href: string }[];
}

interface MobileNavGroup {
  label: string;
  href: string;
  /** Accordion content. A group without sections is a plain link. */
  sections?: MobileNavSection[];
}

// 44px hit area around a 20px glyph. The search button used to be exactly as
// small as its icon, right beside the cart.
const iconButton = "h-11 w-11 items-center justify-center hover:text-primary transition-colors";

// What the phone and tablet menu lists after the rooms. The rooms themselves
// come from the desktop mega menu (see navGroups below).
const moreNavGroups: MobileNavGroup[] = [
  { label: "Room Bundles", href: "/design-your-room" },
  { label: "Collections", href: "/categories" },
  { label: "Best Sellers", href: "/best-sellers" },
  {
    label: "Shop by Price",
    href: "/category/under-100",
    sections: [
      {
        items: [
          { label: "Under $100", href: "/category/under-100" },
          { label: "$100 to $250", href: "/category/100-250" },
          { label: "$250 to $600", href: "/category/250-600" },
          { label: "Table Lamps Under $100", href: "/category/table-lamps-under-100" },
          { label: "Mirrors Under $100", href: "/category/mirrors-under-100" },
          { label: "Throws Under $100", href: "/category/throws-under-100" },
        ],
      },
    ],
  },
  {
    label: "Shop by Style",
    href: "/category/modern-minimalist",
    sections: [
      {
        items: [
          { label: "Modern Minimalist", href: "/category/modern-minimalist" },
          { label: "Warm Earthy", href: "/category/warm-earthy" },
          { label: "Architectural", href: "/category/architectural" },
        ],
      },
    ],
  },
  { label: "New Arrivals", href: "/category/new-arrivals" },
  { label: "Ideas & Inspiration", href: "/ideas" },
  { label: "Get the Look", href: "/get-the-look" },
  { label: "Style Quiz", href: "/style-quiz" },
  { label: "Design Services", href: "/design-services" },
];

// The phone menu ends above the bottom nav. Its old 80vh cap ran under the nav
// on short screens, where the last entry ("Accessibility options", the only way
// to that panel on phones) could not be scrolled into reach. 158px = announcement
// bar (40) + header row (61) + MobileBottomNav (57). Browsers without dvh drop
// this declaration and keep the max-h-[80vh] class.
const MENU_MAX_HEIGHT = "min(80vh, calc(100dvh - 158px - env(safe-area-inset-bottom, 0px)))";

// 44px rows in the phone and tablet menu: they were 18 to 41px tall.
const menuRow = "min-h-11 text-[12px] tracking-[0.16em] uppercase font-bold text-foreground flex items-center gap-2";

// The sign-in dialog brings the Supabase client and the OAuth helper with it
// (a static import here put both back in the startup bundle of every page), so
// it is fetched when a visitor reaches for Sign in.
const loadAuthDialog = () => import("./auth/AuthDialog");
const AuthDialog = lazyRetry(loadAuthDialog);
const warmAuthDialog = () => void loadAuthDialog().catch(() => {});

export const Header = () => {
  useCategoryStockVersion();
  // The rooms are the desktop mega menu's own, already filtered, so phones and
  // tablets reach every category the desktop menu does. The list kept by hand
  // here had drifted to about half of them and left out the largest (Outdoor
  // Rugs, Throw Pillows).
  const navGroups: MobileNavGroup[] = [
    ...visibleMegaMenu()
      .filter((item) => item.columns?.length)
      .map((item) => ({
        label: item.label,
        href: item.href,
        sections: (item.columns ?? []).map((col) => ({ heading: col.heading, items: col.items })),
      })),
    ...moreNavGroups
      .filter((group) => !isEmptyCategoryHref(group.href))
      .map((group) => ({
        ...group,
        sections: group.sections?.map((section) => ({ ...section, items: withoutEmptyCategories(section.items) })),
      })),
  ];
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  // Mounted on first open and kept, so Radix can still play its close animation.
  const [authMounted, setAuthMounted] = useState(false);
  useEffect(() => {
    if (authOpen) setAuthMounted(true);
  }, [authOpen]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const wishlistCount = useWishlistStore((state) => state.items.length);
  const a11yReady = useFloatingPanels((state) => state.a11yReady);
  const openA11y = useFloatingPanels((state) => state.setA11yOpen);
  const openChat = useFloatingPanels((state) => state.setChatOpen);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setOpenAccordion(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      setMobileOpen(false);
      menuButtonRef.current?.focus();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    getSupabase().then((supabase) => {
      if (cancelled) return;
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!cancelled) setIsLoggedIn(!!session);
      });
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_, session) => {
        setIsLoggedIn(!!session);
      });
      unsubscribe = () => subscription.unsubscribe();
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleAccordion = (label: string) => {
    setOpenAccordion((prev) => (prev === label ? null : label));
  };

  // On mobile home top, render header as a transparent overlay above the hero video
  const transparentMobile = isHome && !scrolled && !mobileOpen;

  return (
    <>
      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
      {authMounted && (
        <Suspense fallback={null}>
          <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
        </Suspense>
      )}
      <header
        className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
          transparentMobile
            ? "md:bg-background md:bg-none md:border-b md:border-border bg-gradient-to-b from-black/55 via-black/25 to-transparent border-b border-transparent"
            : "bg-background border-b border-border"
        }`}
      >
        {/* Top sand strip — homepage only, hidden on mobile while overlay is transparent */}
        {isHome && (
          <div className={`bg-sand h-7 flex items-center justify-center gap-2 text-[11px] tracking-[0.16em] uppercase font-semibold text-foreground ${transparentMobile ? "hidden md:flex" : ""}`}>
            <BrandDelivery className="h-4 w-4" />
            {FREE_SHIPPING_COPY}
          </div>
        )}

        {/* Main row */}
        <div className="w-[89%] lg:w-[96%] max-w-[1440px] mx-auto h-[60px] flex items-center justify-between gap-2 xl:gap-6">
          {/* Logo */}
          <Link to="/" className="shrink-0 inline-flex min-h-11 flex-col justify-center text-center leading-none" aria-label="LoftLoom homepage">
            {/* White over the hero photo on phones: dark text was lost in the bright image. */}
            <span className={`font-display block text-[22px] sm:text-[28px] tracking-[0.02em] uppercase ${transparentMobile ? "text-white md:text-foreground" : "text-foreground"}`}>
              LoftLoom
            </span>
            <span className="hidden sm:block text-[8px] tracking-[0.52em] uppercase font-semibold text-foreground">
              Interior
            </span>
          </Link>

          {/* MegaMenu (desktop) */}
          <MegaMenu variant="home" />

          {/* Right icons. No gap: the 44px hit areas already space the glyphs, and
              the negative margin keeps the last glyph on the container edge. The
              [&>button] sizes reach the cart trigger, which CartDrawer renders. */}
          <div className={`flex items-center -mr-3 [&>button]:h-11 [&>button]:w-11 ${transparentMobile ? "text-white md:text-foreground" : "text-foreground"}`}>
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className={`inline-flex ${iconButton}`}
            >
              <BrandSearch className="h-5 w-5" />
            </button>
            {isLoggedIn ? (
              <Link
                to="/account"
                aria-label="Account"
                className={`hidden sm:inline-flex ${iconButton}`}
              >
                <BrandAccount className="h-5 w-5" />
              </Link>
            ) : (
              <button
                onClick={() => setAuthOpen(true)}
                onPointerEnter={warmAuthDialog}
                onFocus={warmAuthDialog}
                aria-label="Sign in"
                className={`hidden sm:inline-flex ${iconButton}`}
              >
                <BrandAccount className="h-5 w-5" />
              </button>
            )}
            <Link
              to="/wishlist"
              aria-label="Wishlist"
              className={`hidden sm:inline-flex relative ${iconButton}`}
            >
              <BrandHeart className="h-5 w-5" />
              {wishlistCount > 0 && (
                <span className="absolute right-1.5 top-1.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </Link>
            <CartDrawer />
            {/* From md up the header is solid even at the top of the homepage, so
                the menu icon turns dark there: it was white on white on tablets. */}
            <Button
              ref={menuButtonRef}
              variant="ghost"
              size="icon"
              className={`lg:hidden ${transparentMobile ? "text-white md:text-foreground hover:bg-white/10 hover:text-white md:hover:bg-accent md:hover:text-foreground" : "text-foreground"}`}
              onClick={() => {
                setMobileOpen(!mobileOpen);
                setOpenAccordion(null);
              }}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile nav with accordions */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden border-t border-border bg-background overflow-hidden max-h-[80vh] overflow-y-auto overscroll-contain"
              style={{ maxHeight: MENU_MAX_HEIGHT }}
            >
              <nav className="w-[89%] max-w-[1140px] mx-auto py-3 flex flex-col">
                {navGroups.map((group, i) => (
                  <motion.div
                    key={group.label}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03, duration: 0.25 }}
                    className="border-b border-border/50 last:border-b-0"
                  >
                    {group.sections?.length ? (
                      <>
                        <button
                          onClick={() => toggleAccordion(group.label)}
                          aria-expanded={openAccordion === group.label}
                          className="w-full min-h-11 flex items-center justify-between py-3 text-[11px] tracking-[0.18em] uppercase font-bold text-foreground hover:text-primary transition-colors"
                        >
                          {group.label}
                          <motion.span
                            animate={{ rotate: openAccordion === group.label ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          </motion.span>
                        </button>
                        <AnimatePresence>
                          {openAccordion === group.label && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              {/* One column on phones. On tablets the sections sit side by
                                  side, and a room with a single list flows into three columns. */}
                              <div className="pl-3 pb-3 flex flex-col md:grid md:grid-cols-3 md:gap-x-8 md:gap-y-3 md:items-start">
                                {group.sections.map((section, sectionIndex) => {
                                  const lone = group.sections.length === 1;
                                  return (
                                    <div
                                      key={section.heading ?? sectionIndex}
                                      className={`flex flex-col ${lone ? "md:col-span-3 md:block md:columns-3 md:gap-x-8" : ""}`}
                                    >
                                      {section.heading && !lone && (
                                        <p className="pt-2 md:pt-0 text-[10px] tracking-[0.16em] uppercase font-semibold text-muted-foreground/80">
                                          {section.heading}
                                        </p>
                                      )}
                                      {section.items.map((child) => (
                                        <Link
                                          key={child.href}
                                          to={child.href}
                                          onClick={() => setMobileOpen(false)}
                                          className="flex min-h-11 items-center break-inside-avoid text-[13px] text-muted-foreground hover:text-foreground transition-colors py-1.5"
                                        >
                                          {child.label}
                                        </Link>
                                      ))}
                                    </div>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        to={group.href}
                        onClick={() => setMobileOpen(false)}
                        className={`flex min-h-11 items-center py-3 text-[11px] tracking-[0.18em] uppercase font-bold transition-colors ${
                          group.label === "Sale" ? "text-primary" : "text-foreground hover:text-primary"
                        }`}
                      >
                        {group.label}
                      </Link>
                    )}
                  </motion.div>
                ))}

                {/* Account & Wishlist links */}
                <div className="pt-3 mt-1 border-t border-border flex flex-col">
                  {isLoggedIn ? (
                    <Link
                      to="/account"
                      onClick={() => setMobileOpen(false)}
                      className={menuRow}
                    >
                      <BrandAccount className="h-4 w-4" />
                      My Account
                    </Link>
                  ) : (
                    <button
                      onClick={() => { setMobileOpen(false); setAuthOpen(true); }}
                      className={menuRow}
                    >
                      <BrandAccount className="h-4 w-4" />
                      Sign In
                    </button>
                  )}
                  <Link
                    to="/wishlist"
                    onClick={() => setMobileOpen(false)}
                    className={menuRow}
                  >
                    <BrandHeart className="h-4 w-4" />
                    Wishlist {wishlistCount > 0 && `(${wishlistCount})`}
                  </Link>
                  {/* The homepage has no bottom nav and no floating buttons, so its
                      menu is the way to the chat. */}
                  {isHome && (
                    <button
                      onClick={() => { setMobileOpen(false); openChat(true, menuButtonRef.current); }}
                      aria-haspopup="dialog"
                      className={menuRow}
                    >
                      <MessageCircle className="h-4 w-4" />
                      Chat
                    </button>
                  )}
                  {/* Phones have no floating accessibility button (it sat on the
                      product price); this opens the same panel. Focus returns to
                      the menu button on close: this entry is gone by then. On the
                      homepage no width has that button, so tablets get the entry too. */}
                  {a11yReady && (
                    <button
                      onClick={() => { setMobileOpen(false); openA11y(true, menuButtonRef.current); }}
                      aria-haspopup="dialog"
                      className={`${isHome ? "" : "md:hidden "}${menuRow}`}
                    >
                      <Accessibility className="h-4 w-4" />
                      Accessibility options
                    </button>
                  )}
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};
